import React, { useState } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import useQueryRMSCommonDataHook from '@fd/customHooks/useQueryRMSCommonDataHook';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import makeStyles from '@mui/styles/makeStyles';
import { Translate } from 'react-localize-redux';
import { useLocation } from 'react-router-dom';

import { MessageBanner } from '@fd/ui/atoms';
import PageLayout from '@fd/ui/Layout';
import PaperContainer from '@fd/ui/Layout/PaperContainer';
import Tabs from '@fd/ui/Tabs';

import ReportsMenuItemsList from '../ReportsMenuItemsList';

const useStyles = makeStyles(() => ({
  loadingSpinner: {
    margin: '100px auto',
  },
}));

const Dashboards = () => {
  const appId = useAppSelector((state) => state.currentApp.AppId);
  const rmsData = useQueryRMSCommonDataHook(appId || '');
  const [hasError, setHasError] = useState(false);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const currentTab = params.get('tab') || 'main';
  const classes = useStyles();

  if (!appId) {
    return <CircularProgress size={40} className={classes.loadingSpinner} />;
  }

  // Check if we have RMS subscriptions to show tabs
  const rmsSubscriptions = rmsData.data?.subscriptions || [];
  const showRms = rmsSubscriptions.length > 0;

  const handleError = (error: unknown) => {
    if (error) {
      setHasError(true);
    }
  };

  const mainReports = (
    <PaperContainer fluid>
      <ReportsMenuItemsList appId={appId} reportType="main" onError={handleError} />
    </PaperContainer>
  );

  const rmsReports = (
    <PaperContainer fluid>
      <ReportsMenuItemsList appId={appId} reportType="rms" onError={handleError} />
    </PaperContainer>
  );

  // Only show error alert on main tab
  const shouldShowErrorAlert = hasError && currentTab === 'main';

  return (
    <PageLayout title={<Translate id="All_dashboards" />} documentTitle="All_dashboards">
      {shouldShowErrorAlert && (
        <MessageBanner
          fdKey="some-reports-failed-to-load"
          variant="error"
          message={<Translate id="We_couldnt_load_all_of_your_reports" />}
          rounded={true}
          icon={<CancelOutlinedIcon />}
        />
      )}
      {showRms ? (
        <Tabs
          tabItems={[
            {
              label: 'Main',
              component: mainReports,
              queryString: 'main',
            },
            {
              label: 'RMS',
              component: rmsReports,
              queryString: 'rms',
            },
          ]}
        />
      ) : (
        mainReports
      )}
    </PageLayout>
  );
};

export default Dashboards;
